// apps/client/src/ui-kit/BottomSheet.tsx
//
// Slide-up panel for rescue flows, confirmations, and contextual actions.
// Covers bottom half of screen, backdrop blur, swipe-to-dismiss.
// Used when user needs to make a decision without leaving the current context.
//

import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import React from 'react';

interface BottomSheetProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
}

export function BottomSheet({ isOpen, onClose, children, title }: BottomSheetProps) {
    const handleDragEnd = (_: unknown, info: PanInfo) => {
        if (info.offset.y > 100 || info.velocity.y > 500) {
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                    />

                    {/* Sheet */}
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                        drag="y"
                        dragConstraints={{ top: 0 }}
                        dragElastic={0.2}
                        onDragEnd={handleDragEnd}
                        className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-[2rem] max-h-[80vh] overflow-y-auto"
                    >
                        {/* Drag handle */}
                        <div className="flex justify-center pt-3 pb-2">
                            <div className="w-10 h-1 rounded-full bg-gray-300" />
                        </div>

                        {/* Title */}
                        {title && (
                            <h3 className="text-lg font-black text-[#1A0B2E] text-center px-6 pb-2">
                                {title}
                            </h3>
                        )}

                        {/* Content */}
                        <div className="px-6 pb-8">
                            {children}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
